import { formatDistanceToNow, parseISO, format } from "date-fns";
import {
  Instagram, Facebook, Youtube, Twitter, Globe, Newspaper,
  Music2, AtSign, Mic, MessagesSquare, BookOpen,
} from "lucide-react";

export const PLATFORM_META = {
  instagram: { label: "Instagram", icon: Instagram, color: "#E1306C" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877F2" },
  tiktok: { label: "TikTok", icon: Music2, color: "#25F4EE" },
  x: { label: "X", icon: Twitter, color: "#E7E9EA" },
  threads: { label: "Threads", icon: AtSign, color: "#B79BFF" },
  youtube: { label: "YouTube", icon: Youtube, color: "#FF0000" },
  news: { label: "News", icon: Newspaper, color: "#D4A359" },
  web: { label: "Web", icon: Globe, color: "#7FB2E0" },
  podcasts: { label: "Podcasts", icon: Mic, color: "#9B87F5" },
  reddit: { label: "Reddit", icon: MessagesSquare, color: "#FF4500" },
  media_guide: { label: "Media Guide", icon: BookOpen, color: "#E8833A" },
};

export function platformMeta(p) {
  return PLATFORM_META[p] || { label: p, icon: Globe, color: "#9A9CA6" };
}

// Map a discovery stage string to a scan progress step (pass N of 3 + short label).
export const SCAN_TOTAL_STEPS = 3;
export function scanPass(stage) {
  const s = (stage || "").toLowerCase();
  if (s.includes("broad sweep")) return { n: 1, total: 3, short: "Broad sweep", pct: 15 };
  if (s.includes("deep dive")) return { n: 2, total: 3, short: "Deep dive", pct: 45 };
  if (s.includes("official record")) return { n: 3, total: 3, short: "Official record", pct: 75 };
  if (s.includes("analyzing") || s.includes("saving")) return { n: 3, total: 3, short: "Saving intel", pct: 90 };
  if (s.includes("presenter brief")) return { n: 3, total: 3, short: "Writing brief", pct: 96 };
  if (s.includes("queued")) return { n: 0, total: 3, short: "Queued", pct: 0 };
  return { n: 0, total: 3, short: stage ? stage.replace(/…$/, "") : "Scanning", pct: 8 };
}

// Rough per-athlete scan time (3 grounded passes + brief synthesis).
export const SECONDS_PER_ATHLETE = 240;
// Rough Universal-Key credit cost per athlete scan (3 grounded Gemini passes + 1 synthesis).
export const CREDITS_PER_ATHLETE = { low: 2, high: 5 };
export function creditRange(count) {
  return {
    low: Math.max(1, Math.round(count * CREDITS_PER_ATHLETE.low)),
    high: Math.max(1, Math.round(count * CREDITS_PER_ATHLETE.high)),
  };
}
export function etaLabel(seconds) {
  if (!seconds || seconds <= 0) return "";
  const m = Math.round(seconds / 60);
  if (m < 1) return "under a minute";
  if (m < 60) return `~${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `~${h}h ${rem}m` : `~${h}h`;
}

export function relativeDate(dateStr) {
  if (!dateStr) return "date unknown";
  try {
    let s = dateStr;
    if (s.length === 7) s = `${s}-01`; // YYYY-MM
    if (s.length === 4) s = `${s}-01-01`;
    const d = parseISO(s);
    return formatDistanceToNow(d, { addSuffix: true });
  } catch {
    return dateStr;
  }
}

export function exactDate(dateStr) {
  if (!dateStr) return "";
  try {
    let s = dateStr;
    if (s.length === 7) s = `${s}-01`;
    if (s.length === 4) s = `${s}-01-01`;
    return format(parseISO(s), "MMM d, yyyy");
  } catch {
    return dateStr;
  }
}

export const CONFIDENCE_STYLES = {
  Official: "bg-[#122A1E] text-[#4ADE80] border-[#1B4D36]",
  "Athlete's own account": "bg-[#0F1B18] text-[#34D399] border-[#1B4D3B]",
  "Established media": "bg-[#12202A] text-[#7FB2E0] border-[#1B3A4D]",
  "Third-party": "bg-[#2A2312] text-[#FACC15] border-[#4D3E1B]",
  Unverified: "bg-[#1A202C] text-[#94A3B8] border-[#334155]",
  // legacy labels (older scans)
  Confirmed: "bg-[#122A1E] text-[#4ADE80] border-[#1B4D36]",
  Observed: "bg-[#2A2312] text-[#FACC15] border-[#4D3E1B]",
  Reported: "bg-[#2A1812] text-[#FB923C] border-[#4D261B]",
};

export const VALUE_STYLES = {
  High: "bg-[#3B1812] text-[#FF6B4A] border-[#632519]",
  Medium: "bg-[#2E2511] text-[#EAB308] border-[#524116]",
  Low: "bg-[#1A202C] text-[#94A3B8] border-[#334155]",
};

const VALUE_WEIGHT = { High: 3, Medium: 2, Low: 1 };
const CONF_WEIGHT = {
  Official: 4, "Athlete's own account": 4, "Established media": 3, "Third-party": 1,
  Confirmed: 3, Reported: 2, Observed: 1, Unverified: 0,
};

function eventTimestamp(ev) {
  const d = ev.event_date || (ev.sources?.[0]?.published_at ?? "");
  if (!d) return 0;
  try {
    let s = d;
    if (s.length === 7) s = `${s}-01`;
    if (s.length === 4) s = `${s}-01-01`;
    return parseISO(s).getTime();
  } catch {
    return 0;
  }
}

export function freshnessScore(ev) {
  const ts = eventTimestamp(ev);
  if (!ts) return 0;
  const days = (Date.now() - ts) / (1000 * 60 * 60 * 24);
  if (days < 7) return 4;
  if (days < 30) return 3;
  if (days < 90) return 2;
  if (days < 365) return 1;
  return 0;
}

export function rankEvents(events) {
  return [...events].sort((a, b) => {
    const sa = freshnessScore(a) * 2 + (VALUE_WEIGHT[a.interview_value] || 0) + (CONF_WEIGHT[a.confidence] || 0);
    const sb = freshnessScore(b) * 2 + (VALUE_WEIGHT[b.interview_value] || 0) + (CONF_WEIGHT[b.confidence] || 0);
    if (sb !== sa) return sb - sa;
    return eventTimestamp(b) - eventTimestamp(a);
  });
}

export const DISCIPLINE_COLORS = {
  "Saddle Bronc": "#E8833A",
  "Bareback": "#D4A359",
  "Bull Riding": "#FF6B4A",
  "Tie-Down Roping": "#7FB2E0",
  "Steer Wrestling": "#9B87F5",
  "Barrel Racing": "#E1306C",
  "Breakaway Roping": "#4ADE80",
  "Team Roping (Header)": "#FACC15",
  "Team Roping (Heeler)": "#EAB308",
};
